import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import ClientShell from "@/components/ClientShell";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/hooks/useCategories";
import { ArrowLeft, ChatCircleText, Package } from "@phosphor-icons/react";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const { isGuest } = useAuth();
  const { byId, loading: catLoading } = useCategories(isGuest);
  const category = byId(categoryId);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    if (!categoryId) return;
    setLoading(true);
    try {
      const res = await api.get(isGuest ? "/public/products" : "/products", { params: { category: categoryId } });
      setProducts(res.data);
    } catch (e) {
      console.error("[CategoryPage] fetch failed:", e);
    }
    setLoading(false);
  }, [categoryId, isGuest]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  if (catLoading) {
    return <ClientShell><div className="h-40 bg-white border border-zinc-200 animate-pulse" /></ClientShell>;
  }
  if (!category) {
    return (
      <ClientShell>
        <div className="py-16 text-center">
          <div className="text-zinc-500">Category not found.</div>
          <Link to="/home" className="mt-4 inline-block underline">Back to home</Link>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell>
      <Link
        to="/home"
        data-testid="category-back-link"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>

      <div className="mb-8">
        <div className={`inline-block ${category.blob} px-3 py-1 mt-tiny ${category.fg}`}>{category.label}</div>
        <h1 className="font-display text-3xl md:text-4xl font-black text-zinc-900 mt-3 tracking-tight">
          {category.label}
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Colours, sizes, and finishes vary — request a quote and we'll share the full spec sheet + certificates.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {["s-1", "s-2", "s-3", "s-4", "s-5"].map((k) => (
            <div key={k} className="aspect-square bg-white border border-zinc-200 animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="grid gap-4">
          <div className="bg-white border border-zinc-200 p-6 md:p-8 flex items-center gap-4">
            <div className={`w-14 h-14 ${category.blob} ${category.fg} grid place-items-center shrink-0`}>
              <Package size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-zinc-900">Product listing coming soon</div>
              <div className="mt-1 text-sm text-zinc-500">
                Our {category.label.toLowerCase()} range will appear here.
              </div>
            </div>
          </div>
          <Link
            to={`/quotations/new?category=${category.id}`}
            data-testid="request-quote-cta"
            className="bg-zinc-900 hover:bg-zinc-800 text-white p-6 md:p-8 flex items-center gap-4 transition-colors"
          >
            <div className="w-14 h-14 bg-[#FF5A1F] text-white grid place-items-center shrink-0">
              <ChatCircleText size={26} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-white">Request a custom quote</div>
              <div className="mt-1 text-sm text-white/70">
                Share your material, quantity and dimensions — we'll get back with a quote and certificates.
              </div>
            </div>
            <div className="text-white/70">→</div>
          </Link>
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
          data-testid="products-grid"
        >
          {products.map((p) => (
            <Link
              key={p.product_id}
              to={`/products/${p.product_id}`}
              data-testid={`product-card-${p.product_id}`}
              className="group relative aspect-square overflow-hidden border border-zinc-200 hover:border-zinc-900 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              style={{ borderRadius: "10px" }}
            >
              <AuthImage
                path={p.image_path}
                alt={p.name}
                guest={isGuest}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                fallback={
                  <div className={`absolute inset-0 ${category.blob} grid place-items-center`}>
                    <Package size={48} className={category.accent_fg} weight="duotone" />
                  </div>
                }
              />

              {/* Dark gradient overlay for legibility */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />

              {/* Product name */}
              <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
                <div className="font-display font-bold text-white text-base md:text-lg leading-tight drop-shadow-lg line-clamp-2">
                  {p.name}
                </div>
                {p.brand && (
                  <div className="mt-0.5 text-[0.7rem] uppercase tracking-[0.18em] text-white/70 truncate">
                    {p.brand}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </ClientShell>
  );
}
